<template>
  <div id="app">
    <div class="menu">
      <!-- <ul class="runs">
        <li class="run" v-for="activity in activities" :key="activity.id">
          <input
            type="checkbox"
            name=""
            :id="activity.id"
            :checked="activity.selected"
            @change="onActivityChange(activity.id, $event.target.checked)"
          />
          <label :for="activity.id">{{ activity.name }}</label>
        </li>
      </ul> -->
      <navigation :active-item="activeSection" @active-item="onMenuChange" />
      <transition name="fade" mode="out-in">
        <div
          key="1"
          v-if="activeSection === 'Activities'"
          class="file-upload-wrapper"
        >
          <div
            :class="['file-upload', { 'is-dragging': isDragging }]"
            @click="onFileUploadClick"
            @drop="onDrop"
            @dragover="onDragOver"
            @dragenter="onDragEnter"
            @dragleave="onDragLeave"
          >
            <div class="icons">
              <file-icon class="file-icon icon-left" width="30" />
              <file-icon class="file-icon icon-right" width="30" />
              <file-icon class="file-icon icon-main" width="50" />
            </div>
            <p>
              drop your <strong>.gpx</strong> file here or
              <strong>browse</strong>
            </p>
            <input
              ref="fileInput"
              type="file"
              accept=".gpx"
              @input="onUpload"
            />
          </div>

          <collapse-transition @after-enter="onAfterEnter">
            <div class="activity-row" v-for="item in activities" :key="item.id">
              <div>
                <span>{{ item.name }}</span>
                <remove-icon
                  class="delete"
                  width="20"
                  @click.native="onRemove(item.id)"
                />
              </div>
            </div>
          </collapse-transition>
        </div>
        <div
          key="2"
          v-if="activeSection === 'Appearance'"
          class="file-upload-wrapper"
        >
          <div class="row">
            <p>Line color</p>
            <div
              class="color-picker"
              :style="colorPickerStyle"
              @click="$refs.colorPicker.click()"
            >
              <input
                ref="colorPicker"
                type="color"
                name="colorPicker"
                id="colorPicker"
                :value="routeColor"
                @input="onColorChange"
              />
            </div>
          </div>
          <div class="row">
            <p>Line width</p>
            <input
              type="range"
              id="range"
              name="range"
              min="1"
              max="8"
              step="0.5"
              :value="lineWidth"
              :style="rangeStyle"
              @input="onLineWidth"
            />
          </div>

          <div class="column">
            <p>Title</p>
            <input
              type="text"
              name="title"
              id="title"
              v-model="_activityTitle"
            />
          </div>

          <div class="column">
            <p>Subtitle</p>
            <input
              type="text"
              name="subtitle"
              id="subtitle"
              v-model="_activitySubtitle"
            />
          </div>
        </div>

        <div
          key="3"
          v-if="activeSection === 'Print/Export'"
          class="file-upload-wrapper"
        >
          <button type="button" class="generate-button" @click="onPrintMap">
            download
          </button>
        </div>
      </transition>
    </div>

    <div class="map-wrapper">
      <div id="map" ref="map"></div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import { toMillimiter } from './utils/export';
import { sample } from './utils/sample';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import simplifyGeojson from 'simplify-geojson';
import gpxParser from 'gpxparser';
import { nanoid } from 'nanoid';
import FileIcon from './components/file-icon.vue';
import RemoveIcon from './components/remove-icon.vue';
import CollapseTransition from './components/collapse-transition.vue';
import Navigation from './components/navigation.vue';

export default {
  name: 'App',
  components: { FileIcon, CollapseTransition, RemoveIcon, Navigation },

  data() {
    return {
      map: null,
      isDragging: false,
      activeSection: 'Activities'
    };
  },

  mounted() {
    mapboxgl.accessToken =
      'pk.eyJ1IjoiZ2FzcGFycm9iaSIsImEiOiJjamo2MzM2amYxems2M3BzMmpkZDYyaTN0In0.96VTDGeFhnhI_cknbHG1aw';
    this.map = new mapboxgl.Map({
      container: 'map', // container ID
      style: 'mapbox://styles/gasparrobi/ckw4pehnx62p014o521hhn6hs', // style URL
      center: [19.052805708733253, 47.546841131997695], // starting position [lng, lat]
      zoom: 11, // starting zoom
      attributionControl: false
    });
    this.map.on('load', () => {
      if (this.activities.length < 1) return this.onAddSample();
      this.activities.forEach((activity) => {
        this.addRun(activity.id, activity.activity);
      });
    });
  },

  computed: {
    ...mapGetters([
      'activities',
      'selectedActivities',
      'isSampleRunAdded',
      'routeColor',
      'lineWidth',
      'activityTitle',
      'activitySubtitle'
    ]),

    colorPickerStyle() {
      return { backgroundColor: this.routeColor };
    },

    rangeStyle() {
      return {
        backgroundSize: ((this.lineWidth - 1) * 100) / (8 - 1) + '% 100%'
      };
    },

    _activityTitle: {
      get() {
        return this.activityTitle;
      },

      set(value) {
        this.setActivityTitle(value);
      }
    },

    _activitySubtitle: {
      get() {
        return this.activitySubtitle;
      },

      set(value) {
        this.setActivitySubtitle(value);
      }
    }
  },

  methods: {
    ...mapMutations([
      'updateEventSelected',
      'addActivity',
      'removeActivity',
      'setRouteColor',
      'setLineWidth',
      'setActivityTitle',
      'setActivitySubtitle'
    ]),

    ...mapActions(['createPrintMap']),

    onMenuChange(value) {
      this.activeSection = value;
    },

    addRun(id, data) {
      this.map.addSource(id, {
        type: 'geojson',
        data
      });
      this.addLayer(id);
    },

    addLayer(id) {
      this.map.addLayer({
        id: id,
        type: 'line',
        source: id,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': this.routeColor,
          'line-width': this.lineWidth,
          'line-opacity': 0.5
        }
      });
    },

    onPrintMap() {
      const { width, height } = this.$refs.map.getBoundingClientRect();
      const bearing = this.map.getBearing();
      const pitch = this.map.getPitch();
      const bounds = this.map.getBounds();

      this.createPrintMap({
        width: toMillimiter(width),
        height: toMillimiter(height),
        bounds,
        pitch,
        bearing
      });
    },

    onAddSample() {
      const id = nanoid(10);
      const activity = sample();

      this.addActivity({
        id,
        activity,
        name: 'sample run',
        selected: true
      });

      this.addRun(id, activity);

      // const id2 = nanoid(10);
      // const activity2 = f1circuits();

      // this.addActivity({
      //   id: id2,
      //   activity: activity2,
      //   name: 'f1 circuits',
      //   selected: true
      // });

      // this.addRun(id2, activity2);
    },

    onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      this.upload(event.dataTransfer.files);
    },

    onDragOver(event) {
      event.preventDefault();
    },

    onDragEnter() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onUpload(event) {
      this.upload(event.target.files);
      event.target.value = null;
    },

    upload(files) {
      for (const file of files) {
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onloadend = () => {
          const gpx = new gpxParser();
          gpx.parse(reader.result);
          const name = gpx.tracks?.[0]?.name.split(' ').join('-');
          const { lat, lon } = gpx.tracks?.[0]?.points?.[0];
          if (!name) return;

          let geoJSON = gpx.toGeoJSON();
          const geo = simplifyGeojson(geoJSON, 0.0001);
          const id = nanoid(10);

          this.addActivity({
            id,
            name,
            activity: geo,
            selected: true
          });

          this.addRun(id, geo);

          this.map.flyTo({ center: [lon, lat] });
        };
      }
      // const file = this.gpx.parse(event.target.files[0]);
      // console.log(file);
    },

    onActivityChange(id, isChecked) {
      this.updateEventSelected({ id, isChecked });
      if (isChecked) return this.addLayer(id);
      else this.map.removeLayer(id);
    },

    onFileUploadClick() {
      this.$refs.fileInput.click();
    },

    onRemove(id) {
      this.map.removeLayer(id);
      this.removeActivity(id);
    },

    onAfterEnter() {},

    onColorChange(event) {
      this.setRouteColor(event.target.value);
      this.activities.forEach((activity) => {
        this.map.setPaintProperty(
          activity.id,
          'line-color',
          event.target.value
        );
      });
    },

    onLineWidth(event) {
      this.setLineWidth(Number(event.target.value));
      this.activities.forEach((activity) => {
        this.map.setPaintProperty(
          activity.id,
          'line-width',
          Number(event.target.value)
        );
      });
    }
  },

  watch: {}
};
</script>

<style lang="scss">
* {
  box-sizing: border-box;
}

html,
body {
  margin: 0 auto;
  padding: 0;
  background-color: #f5f5f5;
}

ul {
  list-style: none;
  padding: 0;
}

li {
}

#app {
  position: relative;
  display: flex;
  // font-family: 'Avenir', Helvetica, Arial, sans-serif;
  font-family: 'Poppins', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  width: 100%;
  height: 100%;
  min-height: 100vh;
}

.menu {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 40px 20px;
  min-width: 220px;
  max-width: 500px;
  width: 100%;
  z-index: 2;
  filter: drop-shadow(0px 0px 16px rgba(46, 149, 218, 0.1));
  overflow: hidden;

  @media (max-width: 500px) {
    position: absolute;
  }

  .generate-button {
    margin: 0;
    background-color: #4583b6;
    border: none;
    padding: 5px 10px;
    border-radius: 3px;
    color: #fff;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      background-color: #bdbdbd;
    }
  }
}

li.run {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #bdbdbd;
  cursor: pointer;

  label {
    padding-left: 10px;
    cursor: pointer;
  }

  input {
    cursor: pointer;
  }
}

.file-upload-wrapper {
  padding: 20px;
  background-color: #fff;
  border-radius: 10px;
  max-height: 10000px;
  transition: all 300ms ease 0ms;

  .activity-row {
    margin-top: 5px;

    &:first-of-type {
      margin-top: 20px;
    }

    div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 12px;
      padding: 15px 10px;
      border: 1px solid #ebebeb;
      border-radius: 10px;
      background: linear-gradient(to right, #ecf3ff 50%, transparent 50%);
      background-size: 201% 100%;
      background-position: right bottom;
      animation: uploading 500ms ease-in 300ms forwards;
      overflow: hidden;

      .delete {
        height: auto;
        min-width: 20px;
        transform: translateX(20px);
        opacity: 0;
        animation: slideDown 200ms ease-in-out 800ms forwards;
        cursor: pointer;

        .main-path {
          transition: all 200ms;
        }

        &:hover .main-path {
          fill: rgb(223, 97, 114);
        }
      }
    }
  }
}

@keyframes uploading {
  0% {
    background-position: right bottom;
  }

  100% {
    background-position: left bottom;
  }
}

@keyframes slideDown {
  0% {
    transform: translateX(20px);
    opacity: 0;
  }

  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.file-upload {
  padding: 10px;
  border: 1px dashed;
  border-width: 2px;
  border-radius: 10px;
  border-color: #bdbdbd;
  background-color: #fff;
  transition: all 300ms ease-in-out 0ms;
  cursor: pointer;

  p {
    font-weight: 500;
    font-size: 12px;
    color: #616161;

    strong {
      font-weight: 500;
      color: rgba(11, 36, 117, 0.884);
    }
  }

  &:hover {
    cursor: pointer;
    background-color: #fafafa;

    .icons .file-icon {
      transform: translateY(-8px);

      &.icon-left {
        transform: translateX(-35px) translateY(5px) rotate(-20deg);
      }

      &.icon-right {
        transform: translateX(35px) translateY(5px) rotate(20deg);
      }
    }
  }

  &.is-dragging {
    background-color: #edf2f7;
  }

  input {
    display: none;
  }

  .icons {
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    .file-icon {
      position: absolute;
      filter: drop-shadow(0px 0px 6px rgba(0, 0, 0, 0.1));
      transition: all 300ms ease 0ms;

      &.icon-left,
      &.icon-right {
        filter: grayscale(1) drop-shadow(0px 0px 6px rgba(0, 0, 0, 0.1));
        opacity: 0.8;
      }

      &.icon-left {
      }

      &.icon-right {
      }
    }
  }
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 55px;
  p {
    margin: 0;
  }

  input[type='text'] {
    border: none;
    background-color: #e3e1e1;
    border-radius: 5px;
    padding: 10px;
    height: 40px;
  }

  input[type='range'] {
    -webkit-appearance: none;
    width: 200px;
    height: 7px;
    background: #ececec;
    border-radius: 5px;
    background-image: linear-gradient(#ff4500, #ff4500);
    background-repeat: no-repeat;
  }

  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #ff4500;
    cursor: ew-resize;
    box-shadow: 0 0 2px 0 #555;
    transition: all 0.3s ease-in-out;
  }

  input[type='range']::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    box-shadow: none;
    border: none;
    background: transparent;
  }
}

.column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: 75px;

  p {
    font-size: 13px;
    font-weight: 500;
    margin: 0;
    color: #838383;
  }

  input[type='text'] {
    border: none;
    background-color: #ececec;
    border-radius: 5px;
    padding: 10px;
    height: 40px;
  }
}

.color-picker {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin: 10px 0;
  cursor: pointer;

  input {
    opacity: 0;
    pointer-events: none;
  }
}

.map-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  // padding-top: 40px;
}

#map {
  width: 100%;
  height: 100%;
  min-height: 100vh;

  .mapboxgl-ctrl-logo {
    display: none !important;
    background: none !important;
  }
}

.collapse-transition {
  transition: height 300ms cubic-bezier(0.4, 0, 0.2, 1),
    padding-top 300ms cubic-bezier(0.4, 0, 0.2, 1),
    padding-bottom 300ms cubic-bezier(0.4, 0, 0.2, 1);
}

// .overlay {
//   position: absolute;
//   width: 100%;
//   height: 100%;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   border: 1px solid red;
//   z-index: 2;
//   pointer-events: none;
// }

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.slide-in {
  &-enter-active,
  &-leave-active {
    transition: transform 0.15s ease-in-out;
  }

  &-enter {
    transform: translateX(100%);
  }
  &-leave-to {
    transform: translateX(-100%);
  }

  &-enter-to,
  &-leave {
    transform: translateX(0%);
  }
}
</style>
